/**
 * Project branding utilities for the applicant portal.
 *
 * Translates the branding configuration returned by the context API into
 * CSS custom properties that can be applied to the document root.
 *
 * @module context/project-branding
 */

import type { applicant } from '@noxverify/api-contracts';

type ApplicantBranding = applicant.ApplicantBranding;

/** Subset of branding fields used for CSS variable generation. */
export interface BrandingConfig {
  logoUrl: string | null;
  primaryColor: string | null;
  supportEmail: string | null;
  /**
   * Tenant-configured default locale for the portal (e.g. `"en"`, `"tr"`).
   *
   * Sourced from the canonical {@link ApplicantBranding} contract. Validated
   * against the supported-locale set at the resolution site (see
   * `resolveInitialLocale` in `App.tsx`), so this stays loose-typed as a
   * `string` to mirror the API contract.
   */
  defaultLocale?: string | null;
}

/** Generate CSS custom properties from a branding configuration. */
export function brandingToCssVars(
  branding: BrandingConfig,
): Record<string, string> {
  const vars: Record<string, string> = {};

  if (branding.primaryColor) {
    vars['--nv-primary-color'] = branding.primaryColor;
  }

  return vars;
}

/** Convert a full {@link ApplicantBranding} to the portal's BrandingConfig. */
export function toBrandingConfig(branding: ApplicantBranding): BrandingConfig {
  return {
    logoUrl: branding.logoUrl,
    primaryColor: branding.primaryColor,
    supportEmail: branding.supportEmail,
    defaultLocale: branding.defaultLocale ?? null,
  };
}
